/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <3rd-party library> */
/** biome-ignore-all lint/a11y/useSemanticElements: <3rd-party library> */

import { type PageViewport, type PDFPageProxy, PixelsPerInch, type RenderTask, setLayerDimensions } from 'pdfjs-dist';
import { memo, type RefObject, useCallback, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { Spinner } from '@air/primitive-spinner';

import usePrevious from '../hooks/usePrevious';
import { getMaxCanvasPixels } from '../utils/constants';
import { type IPDFLinkService } from '../utils/IPDFLinkService';
import { type PDFPagesCache } from '../utils/PDFPagesCache';
import { setPDFPageCanvasSize } from '../utils/setPDFPageCanvasSize';
import { PDFPageAnnotationLayer } from './PDFPageAnnotationLayer';
import { PDFPageTextLayer } from './PDFPageTextLayer/PDFPageTextLayer';

export type RenderPDFViewerPageLayers = (props: {
  pageNumber: number;
  containerRef: RefObject<HTMLElement>;
}) => JSX.Element;

type PDFViewerPageProps = {
  scale: number;
  pageNumber: number;
  pdfPageCacheRef: RefObject<PDFPagesCache | undefined>;
  linkService: IPDFLinkService;
  cachedHeight: number;
  renderPageLayers?: RenderPDFViewerPageLayers;
};

const addZoomingAttribute = (element: HTMLElement) => {
  if (!element.hasAttribute('zooming')) {
    element.setAttribute('zooming', 'true');
    element.style.width = '';
    element.style.height = '';
  }
};

const SCALE_DELAY_MS = 400;

export const PDFViewerPage = memo(
  ({ scale, pageNumber, pdfPageCacheRef, linkService, cachedHeight, renderPageLayers }: PDFViewerPageProps) => {
    const canvasRef = useRef<HTMLCanvasElement>();
    const zoomLayerRef = useRef<HTMLCanvasElement | undefined>();
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasWrapperRef = useRef<HTMLDivElement>(null);
    const [pdfPage, setPdfPage] = useState<PDFPageProxy>();
    const [pdfPageViewport, setPdfPageViewport] = useState<PageViewport>();
    const [isLoaded, setIsLoaded] = useState(false);
    const renderTaskRef = useRef<RenderTask>();

    const scaleTimeoutRef = useRef<number>();

    // biome-ignore lint/plugin: inferred as boolean from the false initial value
    const isMounted = useRef(false);

    const scaleRef = useRef<number>();
    scaleRef.current = scale;

    const prevScale = usePrevious(scale);

    useIsomorphicLayoutEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    const clearZoomLayer = useCallback(() => {
      if (!zoomLayerRef.current) {
        return;
      }

      zoomLayerRef.current.width = 0;
      zoomLayerRef.current.height = 0;
      zoomLayerRef.current?.remove();
      zoomLayerRef.current = undefined;
    }, []);

    const cancelRenderTask = useCallback(async () => {
      const currentTask = renderTaskRef.current;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = undefined;

      // renderTaskRef is assigned to another promise, but we still have to catch errors from cancelling the previous one
      currentTask?.promise.catch((error) => {
        if (error.name === 'RenderingCancelledException') {
          return;
        }
        console.error('renderTask.promise error', error);
      });
      window.clearTimeout(scaleTimeoutRef.current);
    }, []);

    const loadPage = useCallback(
      async (renderDelay = 0) => {
        // biome-ignore lint/suspicious/noUnnecessaryConditions: refs are set after mount, Biome reads useRef(null) as always null
        if (!canvasWrapperRef.current || !containerRef.current || !pdfPageCacheRef.current || !scaleRef.current) {
          return;
        }

        if (!zoomLayerRef.current) {
          zoomLayerRef.current = canvasRef.current;
        }

        await cancelRenderTask();

        canvasRef.current = document.createElement('canvas');
        canvasRef.current.setAttribute('role', 'presentation');
        canvasRef.current.hidden = true;

        if (zoomLayerRef.current) {
          addZoomingAttribute(zoomLayerRef.current);
          zoomLayerRef.current.style.position = 'absolute';
        }

        const ctx = canvasRef.current.getContext('2d', { alpha: false, willReadFrequently: true });

        if (!ctx) {
          return;
        }

        const pdfPage = await pdfPageCacheRef.current.getPage(pageNumber);

        if (!pdfPage) {
          return;
        }

        // biome-ignore lint/suspicious/noUnnecessaryConditions: isMounted flips after the await above
        if (!isMounted.current) {
          return;
        }

        const scaleToSet = scaleRef.current * PixelsPerInch.PDF_TO_CSS_UNITS;
        const viewport = pdfPage.getViewport({ scale: scaleToSet });

        setLayerDimensions(containerRef.current, viewport, true, false);
        containerRef.current?.style.setProperty('--scale-factor', scaleToSet.toString());

        window.clearTimeout(scaleTimeoutRef.current);
        scaleTimeoutRef.current = window.setTimeout(() => {
          if (!canvasRef.current || !isMounted.current) {
            return;
          }

          const { transform } = setPDFPageCanvasSize({
            canvas: canvasRef.current,
            height: viewport.height,
            maxCanvasPixels: getMaxCanvasPixels(),
            width: viewport.width,
          });

          renderTaskRef.current = pdfPage.render({ canvasContext: ctx, transform, viewport });
          renderTaskRef.current?.promise.then(
            () => {
              // biome-ignore lint/complexity/noExtraBooleanCast lint/suspicious/noUnnecessaryConditions: refs are set after mount, Biome reads useRef(null) as always null
              if (!!canvasWrapperRef.current && !!canvasRef.current && isMounted.current) {
                canvasWrapperRef.current.appendChild(canvasRef.current);
                canvasRef.current.hidden = false;
                setIsLoaded(true);
                setPdfPage(pdfPage);
                setPdfPageViewport(viewport);
              }
              clearZoomLayer();
            },
            (error) => {
              if (error.name === 'RenderingCancelledException') {
                return;
              }
              console.error('renderTask.promise error', error);
              clearZoomLayer();
            },
          );
        }, renderDelay);
      },
      [cancelRenderTask, clearZoomLayer, pageNumber, pdfPageCacheRef],
    );

    useIsomorphicLayoutEffect(() => {
      void loadPage();

      return () => {
        void cancelRenderTask();
        clearZoomLayer();

        if (canvasRef.current) {
          canvasRef.current.width = 0;
          canvasRef.current.height = 0;
        }
      };
    }, [cancelRenderTask, clearZoomLayer, loadPage, pageNumber, pdfPageCacheRef]);

    const loadPageRef = useRef(loadPage);
    loadPageRef.current = loadPage;

    useIsomorphicLayoutEffect(() => {
      if (!prevScale || scale === prevScale) {
        return;
      }
      // biome-ignore lint/nursery/noFloatingPromises: fire-and-forget reload on scale change
      loadPageRef.current(SCALE_DELAY_MS);
    }, [prevScale, scale]);

    return (
      <div
        className="pdfPage relative mx-auto"
        data-page-number={pageNumber}
        data-testid="PDF_VIEWER_PAGE"
        ref={containerRef}
        role="region"
      >
        <div
          className="pdfCanvasWrapper"
          ref={canvasWrapperRef}
          style={{ height: pdfPageViewport?.height ?? cachedHeight }}
        />
        {!!pdfPage && !!pdfPageViewport && (
          <PDFPageTextLayer pageNumber={pageNumber} pageViewport={pdfPageViewport} pdfPage={pdfPage} />
        )}
        {!!pdfPage && !!pdfPageViewport && (
          <PDFPageAnnotationLayer linkService={linkService} pageViewport={pdfPageViewport} pdfPage={pdfPage} />
        )}
        {renderPageLayers?.({ containerRef: canvasWrapperRef, pageNumber })}
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center ads:bg-ads-gray-12 bg-gray-12">
            <Spinner className="size-8 ads:text-ads-cyan-10 text-teal-9" />
          </div>
        )}
      </div>
    );
  },
);

PDFViewerPage.displayName = 'PDFViewerPage';
